import React, { useRef, useEffect, useState } from 'react';
import styles from '../Request/Request.module.css';
import Loading from '../../../components/loading/Loading';
import MessageDisplay from '../../../components/message/MessageDisplay';
import { SelectedInput } from '../../../components/input/SelectedInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faChevronDown, faUser, faPhone } from '@fortawesome/free-solid-svg-icons';
import useCleanupOnRouteChange from '../../../utilities/Cleanup';
import usePaginatedData from '../../../utilities/Paginationdata';

const RequestList = () => {
    const { data, prevData, page, setPage, isLoading, message, inputValue, handleSearch } = usePaginatedData({
        url: `${process.env.REACT_APP_URL}/api/rides/request`,
        localKeys: {
            search: 'search-request',
            page: 'pageno-request',
            totalPage: 'totalpage-request',
            totalData: 'totaldata-request',
        },
        enableSearch: true,
    });

    useCleanupOnRouteChange(
        ['search-request', 'totalpage-request', 'totaldata-request', 'pageno-request'],
        '/request'
    );

    const firstRowRef = useRef(null);
    const currentData = data[page.pageno] || prevData;
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        if (firstRowRef.current) {
            firstRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [page.pageno]);

    useEffect(() => {
        setExpandedId(null);
    }, [page.pageno, currentData]);

    const parseDrivers = (raw) => {
        let drivers = raw;
        if (typeof drivers === 'string') {
            try { drivers = JSON.parse(drivers); } catch { drivers = []; }
        }
        return (drivers || []).filter(Boolean);
    };

    const formatDate = (dateVal) => {
        if (!dateVal) return '-----';
        const d = new Date(dateVal);
        return isNaN(d) ? dateVal : d.toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const toggleRow = (requestId) => {
        setExpandedId((prev) => (prev === requestId ? null : requestId));
    };

    const renderDriverDetails = (drivers) => (
        drivers.length === 0 ? (
            <div className={styles.nodata}>No drivers were sent for this request</div>
        ) : (
            <table className={styles.driverDetailsTable}>
                <thead>
                    <tr>
                        <th>Driver name</th>
                        <th>Contact</th>
                        <th>Status</th>
                        <th>Sent at</th>
                        <th>Responded at</th>
                    </tr>
                </thead>
                <tbody>
                    {drivers.map((d, i) => (
                        <tr key={i}>
                            <td>{d.driver_name ?? '-----'}</td>
                            <td>{d.driver_contact ?? '-----'}</td>
                            <td>
                                <span className={`${styles.driverStatus} ${styles[d.status] || ''}`}>
                                    {d.status ?? '-----'}
                                </span>
                            </td>
                            <td>{formatDate(d.send_at)}</td>
                            <td>{formatDate(d.responded_at)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )
    );

    return (
        <div className={styles.container}>
            <div className={styles.heading}>
                <h1 className={styles.header}>Request list</h1>
                <SelectedInput
                    value={inputValue}
                    classname={'driverSearch'}
                    placeholder={'Search by user, request ID, or driver name'}
                    onChange={handleSearch}
                />
            </div>

            {isLoading && (
                <div className={styles.loading}>
                    <Loading />
                </div>
            )}

            {message?.type && <MessageDisplay message={message} />}
            {message && currentData.length <= 0 ? (
                <div className={styles.nodata}>{message}</div>
            ) : null}

            {/* Desktop table */}
            {currentData.length > 0 && (
                <div className={styles.tablecontainer}>
                    <table className={`${styles.table} ${isLoading ? styles.blur : ''}`}>
                        <thead>
                            <tr>
                                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'center' }}>S/No</th>
                                <th>User</th>
                                <th>Request ID</th>
                                <th>Medium</th>
                                <th>Date</th>
                                <th>Drivers</th>
                                <th style={{ width: '1%' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentData.map((item, rowIndex) => {
                                const drivers = parseDrivers(item.drivers);
                                const isExpanded = expandedId === item.request_id;
                                return (
                                    <React.Fragment key={item.request_id ?? rowIndex}>
                                        <tr
                                            ref={rowIndex === 0 ? firstRowRef : null}
                                            className={`${styles.row} ${styles.clickableRow} ${isExpanded ? styles.activeRow : ''}`}
                                            onClick={() => toggleRow(item.request_id)}
                                        >
                                            <td className={styles.item}>
                                                {(page.pageno - 1) * 20 + rowIndex + 1}
                                            </td>
                                            <td className={styles.item}>{item.user_name ?? '-----'}</td>
                                            <td className={`${styles.item} ${styles.mono}`}>{item.request_id ?? '-----'}</td>
                                            <td className={`${styles.item} ${styles.mono}`}>{item.vehicle_type ?? '-----'}</td>
                                            <td className={styles.item}>{formatDate(item.date)}</td>
                                            <td className={styles.item}>
                                                <span className={styles.driverCount}>
                                                    {drivers.length}
                                                </span>
                                            </td>

                                        </tr>
                                        {isExpanded && (
                                            <tr className={styles.expandedRow}>
                                                <td colSpan={6}>
                                                    {renderDriverDetails(drivers)}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {page.pageno === page.totalPage && (
                                <tr>
                                    <td className={styles.lastItem} colSpan={6}>
                                        All requests fetched
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Mobile cards */}
            {currentData.length > 0 && (
                <div className={styles.smallScreen}>
                    {currentData.map((item, rowIndex) => {
                        const drivers = parseDrivers(item.drivers);
                        const isExpanded = expandedId === item.request_id;
                        return (
                            <div
                                key={item.request_id ?? rowIndex}
                                className={`${styles.requestCard} ${isExpanded ? styles.requestCardOpen : ''}`}
                                onClick={() => toggleRow(item.request_id)}
                            >
                                <div className={styles.requestCardHeader}>
                                    <div className={styles.requestCardMain}>
                                        <div className={styles.requestCardId}>{item.request_id ?? '-----'}</div>
                                        <div className={styles.requestCardMeta}>
                                            <FontAwesomeIcon icon={faUser} className={styles.metaIcon} />
                                            {item.user_name ?? '-----'}
                                        </div>
                                        <div className={styles.requestCardMeta}>{formatDate(item.date)}</div>
                                    </div>
                                    <div className={styles.requestCardRight}>
                                        <span className={styles.driverCount}>{drivers.length}</span>
                                        <FontAwesomeIcon
                                            icon={faChevronDown}
                                            className={`${styles.expandIcon} ${isExpanded ? styles.expandIconOpen : ''}`}
                                        />
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div className={styles.requestCardDrivers}>
                                        {drivers.length === 0 ? (
                                            <div className={styles.nodata}>No drivers were sent for this request</div>
                                        ) : (
                                            drivers.map((d, i) => (
                                                <div key={i} className={styles.driverMiniCard}>
                                                    <div className={styles.driverMiniTop}>
                                                        <span className={styles.driverMiniName}>{d.driver_name ?? '-----'}</span>
                                                        <span className={`${styles.driverStatus} ${styles[d.status] || ''}`}>
                                                            {d.status ?? '-----'}
                                                        </span>
                                                    </div>
                                                    <div className={styles.driverMiniContact}>
                                                        <FontAwesomeIcon icon={faPhone} className={styles.metaIcon} />
                                                        {d.driver_contact ?? '-----'}
                                                    </div>
                                                    <div className={styles.driverMiniTimes}>
                                                        <span>Sent: {formatDate(d.send_at)}</span>
                                                        <span>Responded: {formatDate(d.responded_at)}</span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {page.pageno === page.totalPage && (
                        <span>All requests fetched</span>
                    )}
                </div>
            )}

            {currentData.length > 0 && (
                <div className={styles.footer}>
                    <h3>Total: {page.totalData}</h3>
                    <div className={styles.pageno}>
                        <button
                            disabled={page.pageno === 1 || isLoading}
                            onClick={() => setPage((prev) => ({ ...prev, pageno: prev.pageno - 1 }))}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span>
                            Page <strong>{page.pageno}</strong> of <strong>{page.totalPage}</strong>
                        </span>
                        <button
                            disabled={page.pageno === page.totalPage || isLoading}
                            onClick={() => setPage((prev) => ({ ...prev, pageno: prev.pageno + 1 }))}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RequestList;